import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { SortByParam, sortByParamConfig } from '@shared/models/sortby-param';
import { Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';

import {
  associationsLoadSelectedCandidateOnAssignsContainerPageChange,
  associationUpdateFilterEffect,
} from '@app/collections/associations';
import { getCandidatesSelectedId } from '@collections/candidates';
import { associationDefaultFilter, isTruthy } from '@shared';
import {
  CandidatePageFilter,
  getCandidatePageKey,
} from '@shared/candidate-filters/candidate-filters.model';
import { Association, AssociationFilter } from '@shared/models';
import { LocalStorageService } from '@shared/services/local-storage.service';

import { getAssignsAssociationsState, getAssignsWithSelectedCandidate } from '../../store';

@Component({
  selector: 'mojo-assigns-candidates-container',
  templateUrl: './candidate-assigns.container.html',
  styleUrls: ['./candidate-assigns.container.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CandidateAssignsContainerComponent implements OnInit {
  emptyMessage = 'No assigned jobs';

  assigns$: Observable<Association[]> = this.store.select(getAssignsWithSelectedCandidate);
  sortFromStorage$ = this.getSortLabelFromStorage();

  pagination$ = this.assigns$.pipe(
    switchMap(() => this.store.select(getAssignsAssociationsState)),
    shareReplay(1),
  );
  totalNumberOfAssigns$ = this.pagination$.pipe(
    map(({ numberOfElements = 0 }) => numberOfElements),
  );

  filters$ = this.store.select(getCandidatesSelectedId).pipe(
    filter(isTruthy),
    take(1),
    map(
      (candidateId) =>
        this.localStorage.getSavedState<CandidatePageFilter>(getCandidatePageKey(candidateId))
          .association,
    ),
  );

  currentFilter: AssociationFilter = associationDefaultFilter;
  sortMenuItems: SortByParam[] = [
    sortByParamConfig.latestApplicationDate,
    sortByParamConfig.oldestApplicationDate,
  ];

  ngOnInit(): void {
    this.filters$.subscribe((currentFilter) => {
      this.currentFilter = currentFilter;
    });
  }

  constructor(private store: Store<{}>, private localStorage: LocalStorageService) {}

  changeFilter(filters: { filterName: string; filterValue: any }[]) {
    filters.forEach((item) => {
      this.currentFilter[item.filterName] = item.filterValue;
    });
    this.dispatchFilter();
  }

  sortBy(sort: SortByParam) {
    this.store.dispatch(
      associationUpdateFilterEffect({
        params: {
          'sort.by': sort.sortBy,
          'sort.direction': sort.direction,
        },
      }),
    );
    this.updateSortFilter(sort);
  }

  private updateSortFilter(sort: SortByParam) {
    this.changeFilter([
      {
        filterName: 'sort.by',
        filterValue: sort.sortBy,
      },
      {
        filterName: 'sort.direction',
        filterValue: sort.direction,
      },
    ]);
  }

  private dispatchFilter() {
    this.store.dispatch(
      associationsLoadSelectedCandidateOnAssignsContainerPageChange({
        currentFilter: this.currentFilter,
      }),
    );
  }

  private getSortLabelFromStorage() {
    return this.store.select(getCandidatesSelectedId).pipe(
      map((candidateId) =>
        this.localStorage.getSavedState<object>(getCandidatePageKey(candidateId)),
      ),
      map((savedState: { association }) =>
        sortByParamConfig.oldestApplicationDate.sortBy === savedState.association['sort.by']
          ? sortByParamConfig.oldestApplicationDate.label
          : sortByParamConfig.latestApplicationDate.label,
      ),
    );
  }

  protected readonly getCandidatePageKey = getCandidatePageKey;
}
